// Default gross margin rates for the IBC + Nano pricing model.
//
// These are fallbacks used by the calculator ONLY while the ingredient_categories data is
// still loading; once loaded, rates come from ingredient_categories.default_margin (editable
// by admins in Admin → Pricing Settings).
//
// Every margin in this app is a gross margin RATE (see utils/marginResolution.js):
//   selling_price = cost / (1 - rate)
// 0.50 => 50% target gross margin (price = 2× cost)
// 0.15 => 15% target gross margin (price ≈ 1.18× cost)
// 0.00 => pass-through at cost
export const DEFAULT_IBC_MARGIN_RATE = 0.15;
export const DEFAULT_NANO_MARGIN_RATE = 0.50;
// Was 0.85 (an 85% margin, 6.67× markup) until 2026-09-08. That number was the
// add_in category's cost-ratio value read as a rate; "Other costs" is meant to
// carry the same 15% margin as the additives in that category. See migration
// 20260908160848_correct_add_in_category_margin.sql.
export const DEFAULT_OTHER_COSTS_MARGIN_RATE = 0.15;

// Margin on a hand-entered ("Other") delivery amount. The typed figure is the
// freight cost; selling it at cost / (1 - 0.15) makes freight carry 15% margin
// instead of passing through at zero. This is the DEFAULT for a new entry on both
// the formulated calculator and a custom-formulation line — clearing the margin
// checkbox bills freight at cost. Matches RESTIM_DEFAULTS.deliveryMarkup and is
// mirrored in netlify/functions/utils/customFormulationPricing.js.
export const CUSTOM_DELIVERY_MARGIN_BUMP_RATE = 0.15;

// How a hand-entered ("Other") delivery amount was keyed: a rate per gallon, or a
// flat figure covering the whole job/line that the pricing math spreads over its
// gallons. Mirrored by VALID_CUSTOM_DELIVERY_UNITS in
// netlify/functions/utils/customFormulationPricing.js and by the
// quote_line_items / quotes custom_delivery_unit CHECK constraints.
export const CUSTOM_DELIVERY_UNITS = [
  { value: 'per_gal', label: '$ / gal' },
  { value: 'flat', label: 'flat $' },
];

// The single client-side resolution of a hand-entered delivery amount into the
// { cost, price } per-gallon pair the rest of the pricing math consumes. Used by
// BOTH the formulated calculator (useCalculator) and each custom-formulation line
// (useCustomFormulation) so the two paths cannot drift apart, and mirrored
// server-side in customFormulationPricing.resolveDelivery.
//
// A flat amount needs the gallon count it is spread over, so callers must pass the
// job's (or line's) gallons; with no gallons yet there is no per-gallon figure and
// both sides are 0 rather than Infinity.
export function resolveCustomDeliveryRates({ amount, unit, marginBump, gallons } = {}) {
  const amt = Number(amount) || 0;
  const gal = Number(gallons) || 0;
  const cost = unit === 'flat' ? (gal > 0 ? amt / gal : 0) : amt;
  const price = marginBump ? cost / (1 - CUSTOM_DELIVERY_MARGIN_BUMP_RATE) : cost;
  return { cost, price };
}

// Legacy aliases retained for components that still import the older names.
export const SURFACTANT_MARGIN = DEFAULT_IBC_MARGIN_RATE;
export const BLENDING_COST_MARGIN = DEFAULT_IBC_MARGIN_RATE;
export const NANOPARTICLE_MARGIN = DEFAULT_NANO_MARGIN_RATE;

// Legacy nano cost constant; real landed cost now comes from the ingredients table
// (cost_per_gallon + shipping_cost_per_gallon). Retained as a fallback only.
export const NANOPARTICLE_BASE_COST = 19.26;
export const NANOPARTICLE_PRICE = NANOPARTICLE_BASE_COST / (1 - DEFAULT_NANO_MARGIN_RATE);

export const GALLONS_PER_BARREL = 42;

// Default completion fluid volume rate. Industry-standard estimate of total
// barrels pumped per foot of lateral; used to seed completion_volume on a
// well when the rep hasn't entered one explicitly. Reps can override per-well
// in the deal Wells section or the calculator.
export const COMPLETION_VOLUME_BBL_PER_FT = 50;

// Fallback product cost/price maps (used only when the DB hasn't loaded yet).
// Real per-product IBC cost lives on blended_products.ibc_cost_per_gallon.
export const PRODUCT_BASE_COSTS = {
  '6101': 7.26,
  '6102': 2.40,
  '6103': 2.40
};

export const PRODUCT_PRICES = {
  '6101': PRODUCT_BASE_COSTS['6101'] / (1 - DEFAULT_IBC_MARGIN_RATE),
  '6102': PRODUCT_BASE_COSTS['6102'] / (1 - DEFAULT_IBC_MARGIN_RATE),
  '6103': PRODUCT_BASE_COSTS['6103'] / (1 - DEFAULT_IBC_MARGIN_RATE)
};

export const PRODUCT_BLENDING_COSTS = {
  '6101': 0,
  '6102': 0,
  '6103': 0
};

export const PRODUCT_CONCENTRATIONS = {
  '6101': 22.5,
  '6102': 15,
  '6103': 15
};

export const PRODUCT_DESCRIPTIONS = {
  '6101': 'Custom surfactant blended with a {concentration}% concentration of nanoActiv.',
  '6102': 'Custom surfactant blended with a {concentration}% concentration of nanoActiv.',
  '6103': 'Custom surfactant blended with a {concentration}% concentration of nanoActiv.'
};

export const PRODUCT_OPTIONS = [
  { value: '6101', label: 'nanoActiv\u2122 FlexSurf 6101' },
  { value: '6102', label: 'nanoActiv\u2122 FlexSurf 6102' },
  { value: '6103', label: 'nanoActiv\u2122 FlexSurf 6103' }
];

export const DELIVERY_COSTS = {
  midland: 0.64,
  delaware: 0.85,
  eagleford: 0.79,
  midcon: 0.61,
  pickup: 0,
  other: 0
};

export const LOCATION_OPTIONS = [
  { value: 'midland', label: 'Midland' },
  { value: 'delaware', label: 'Delaware' },
  { value: 'eagleford', label: 'Eagle Ford' },
  { value: 'midcon', label: 'MidCon' },
  { value: 'pickup', label: 'Pick up' },
  { value: 'other', label: 'Other' }
];

export const MIN_MARGIN_THRESHOLD = 0.85;
export const MAX_MARGIN_TARGET = 0.60;

export const DISCOUNT_AUTHORITY = {
  SALES_REP: { minMargin: 30, label: 'Sales Rep' },
  SALES_MANAGER: { minMargin: 25, label: 'Sales Manager' },
  EXECUTIVE: { minMargin: 0, label: 'CEO' }
};

// Authority is keyed off GROSS MARGIN, not discount amount. Reps can transact at ≥30%
// margin, sales managers down to 25%, and anything below 25% requires CEO approval.
export const getDiscountAuthority = (grossMargin) => {
  if (grossMargin >= DISCOUNT_AUTHORITY.SALES_REP.minMargin) return DISCOUNT_AUTHORITY.SALES_REP.label;
  if (grossMargin >= DISCOUNT_AUTHORITY.SALES_MANAGER.minMargin) return DISCOUNT_AUTHORITY.SALES_MANAGER.label;
  return DISCOUNT_AUTHORITY.EXECUTIVE.label;
};

// Color thresholds for gross margin display:
//   ≥30% green, 25–30% yellow, 20–25% orange, <20% red.
export const getMarginStyle = (grossMargin) => {
  if (grossMargin >= 30) {
    return {
      bg: 'bg-ae-success-bg',
      border: 'border-ae-success',
      text: 'text-crm-success-text',
      value: 'text-crm-success-text',
      tone: 'green',
    };
  }
  if (grossMargin >= 25) {
    return {
      bg: 'bg-yellow-50',
      border: 'border-yellow-400',
      text: 'text-yellow-700',
      value: 'text-yellow-900',
      tone: 'yellow',
    };
  }
  if (grossMargin >= 20) {
    return {
      bg: 'bg-orange-50',
      border: 'border-orange-400',
      text: 'text-orange-700',
      value: 'text-orange-900',
      tone: 'orange',
    };
  }
  return {
    bg: 'bg-red-50',
    border: 'border-activ-red',
    text: 'text-activ-red',
    value: 'text-activ-red',
    custom: true,
    tone: 'red',
  };
};
